import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Trash2, Plus, ChevronRight, Upload, Filter } from 'lucide-react';
import { apiClient } from '@/lib/apiClient';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Breadcrumbs } from '@/components/Breadcrumbs';

// Story 79.1 — Target account list management.
// /admin/target-accounts

interface TargetList {
  id: string;
  name: string;
  description: string | null;
  memberCount: number;
}

interface ListMember {
  accountId: string;
  accountName: string;
  industry: string | null;
  sizeBucket: string | null;
  addedAt: string;
}

interface ListDetail extends TargetList {
  members: ListMember[];
}

interface BulkResult {
  added: number;
  skipped: number;
  unmatched: string[];
}

export function TargetAccountsPage() {
  const { t } = useTranslation();
  const [lists, setLists] = useState<TargetList[]>([]);
  const [selected, setSelected] = useState<ListDetail | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showSegment, setShowSegment] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createDesc, setCreateDesc] = useState('');
  const [creating, setCreating] = useState(false);
  const [bulkResult, setBulkResult] = useState<BulkResult | null>(null);
  const [segmentIndustries, setSegmentIndustries] = useState('');
  const [segmentSizes, setSegmentSizes] = useState('');
  const [segmenting, setSegmenting] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const loadLists = useCallback(async () => {
    try {
      const { data } = await apiClient.get<TargetList[]>('/target-account-lists');
      setLists(data);
    } catch {
      setLists([]);
    }
  }, []);

  const loadDetail = useCallback(async (id: string) => {
    try {
      const { data } = await apiClient.get<ListDetail>(`/target-account-lists/${id}`);
      setSelected(data);
    } catch {
      setSelected(null);
    }
  }, []);

  useEffect(() => {
    loadLists();
  }, [loadLists]);

  async function createList() {
    if (!createName.trim()) return;
    setCreating(true);
    try {
      await apiClient.post('/target-account-lists', {
        name: createName.trim(),
        description: createDesc.trim() || null,
      });
      setCreateName('');
      setCreateDesc('');
      setShowCreate(false);
      await loadLists();
    } finally {
      setCreating(false);
    }
  }

  async function deleteList(id: string) {
    if (!confirm(t('targetAccounts.confirmDelete'))) return;
    await apiClient.delete(`/target-account-lists/${id}`);
    if (selected?.id === id) setSelected(null);
    await loadLists();
  }

  async function removeMember(listId: string, accountId: string) {
    await apiClient.delete(`/target-account-lists/${listId}/members/${accountId}`);
    if (selected) await loadDetail(selected.id);
    await loadLists();
  }

  async function uploadCsv(listId: string, file: File) {
    const form = new FormData();
    form.append('file', file);
    try {
      const { data } = await apiClient.post<BulkResult>(
        `/target-account-lists/${listId}/members/csv`,
        form,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      setBulkResult(data);
      await loadDetail(listId);
      await loadLists();
    } catch {
      setBulkResult(null);
    }
  }

  async function applySegment(listId: string) {
    setSegmenting(true);
    try {
      const industries = segmentIndustries
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      const sizeBuckets = segmentSizes
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      const { data } = await apiClient.post<BulkResult>(
        `/target-account-lists/${listId}/members/segment`,
        { industries, sizeBuckets },
      );
      setBulkResult(data);
      setShowSegment(false);
      await loadDetail(listId);
      await loadLists();
    } finally {
      setSegmenting(false);
    }
  }

  return (
    <div className="space-y-6">
      <Breadcrumbs crumbs={[{ label: t('targetAccounts.title') }]} />

      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">{t('targetAccounts.title')}</h1>
        <Button size="sm" onClick={() => setShowCreate(true)}>
          <Plus className="mr-1 h-4 w-4" />
          {t('targetAccounts.newList')}
        </Button>
      </div>

      {showCreate && (
        <div className="rounded-lg border bg-card p-4 space-y-3">
          <Input
            placeholder={t('targetAccounts.listName')}
            value={createName}
            onChange={(e) => setCreateName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && createList()}
          />
          <Input
            placeholder={t('targetAccounts.description')}
            value={createDesc}
            onChange={(e) => setCreateDesc(e.target.value)}
          />
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="outline" onClick={() => setShowCreate(false)} disabled={creating}>
              {t('sequenceEnrollment.cancel')}
            </Button>
            <Button size="sm" onClick={createList} disabled={creating || !createName.trim()}>
              {t('targetAccounts.createList')}
            </Button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {/* List panel */}
        <div className="space-y-2">
          {lists.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('targetAccounts.noLists')}</p>
          ) : (
            lists.map((list) => (
              <div
                key={list.id}
                onClick={() => { setBulkResult(null); loadDetail(list.id); }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    setBulkResult(null);
                    loadDetail(list.id);
                  }
                }}
                className={`flex cursor-pointer items-center justify-between rounded-lg border p-3 transition-colors hover:bg-muted/40 ${
                  selected?.id === list.id ? 'border-primary bg-primary/5' : ''
                }`}
              >
                <div className="min-w-0">
                  <div className="truncate font-medium">{list.name}</div>
                  <div className="text-xs text-muted-foreground">
                    {t('targetAccounts.memberCount', { count: list.memberCount })}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={(e) => { e.stopPropagation(); deleteList(list.id); }}
                    className="rounded p-1 text-destructive/60 hover:bg-destructive/10 hover:text-destructive"
                    title={t('targetAccounts.deleteList')}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </div>
              </div>
            ))
          )}
        </div>

        {/* Detail panel */}
        {selected && (
          <div className="md:col-span-2 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold">{selected.name}</h2>
                {selected.description && (
                  <p className="text-sm text-muted-foreground">{selected.description}</p>
                )}
              </div>
              <div className="flex gap-2">
                {/* CSV upload */}
                <input
                  ref={fileRef}
                  type="file"
                  accept=".csv,.txt"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file && selected) uploadCsv(selected.id, file);
                    if (fileRef.current) fileRef.current.value = '';
                  }}
                />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => fileRef.current?.click()}
                >
                  <Upload className="mr-1 h-3.5 w-3.5" />
                  {t('targetAccounts.bulkCsv')}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setShowSegment((v) => !v)}
                >
                  <Filter className="mr-1 h-3.5 w-3.5" />
                  {t('targetAccounts.bulkSegment')}
                </Button>
              </div>
            </div>

            {/* Segment filter panel */}
            {showSegment && (
              <div className="rounded-lg border bg-muted/30 p-3 space-y-2">
                <Input
                  placeholder={t('targetAccounts.industries') + ' (comma-separated)'}
                  value={segmentIndustries}
                  onChange={(e) => setSegmentIndustries(e.target.value)}
                />
                <Input
                  placeholder={t('targetAccounts.sizeBuckets') + ' (comma-separated)'}
                  value={segmentSizes}
                  onChange={(e) => setSegmentSizes(e.target.value)}
                />
                <div className="flex justify-end gap-2">
                  <Button size="sm" variant="outline" onClick={() => setShowSegment(false)}>
                    {t('sequenceEnrollment.cancel')}
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => applySegment(selected.id)}
                    disabled={segmenting}
                  >
                    {t('targetAccounts.applySegment')}
                  </Button>
                </div>
              </div>
            )}

            {/* Bulk result banner */}
            {bulkResult && (
              <div className="rounded-lg border border-emerald-800 bg-emerald-950/40 px-3 py-2 text-sm text-emerald-100">
                {t('targetAccounts.addedResult', {
                  added: bulkResult.added,
                  skipped: bulkResult.skipped,
                })}
                {bulkResult.unmatched.length > 0 && (
                  <span className="ml-2 text-muted-foreground">
                    {t('targetAccounts.unmatched', {
                      names: bulkResult.unmatched.slice(0, 5).join(', ') +
                        (bulkResult.unmatched.length > 5
                          ? ` +${bulkResult.unmatched.length - 5}`
                          : ''),
                    })}
                  </span>
                )}
              </div>
            )}

            {/* Members table */}
            {selected.members.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('targetAccounts.noMembers')}</p>
            ) : (
              <div className="rounded-lg border">
                <table className="w-full text-sm">
                  <thead className="border-b bg-muted/30">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium">{t('nav.accounts')}</th>
                      <th className="px-3 py-2 text-left font-medium">{t('targetAccounts.industry')}</th>
                      <th className="px-3 py-2 text-left font-medium">{t('targetAccounts.sizeBucket')}</th>
                      <th className="px-3 py-2 text-left font-medium">{t('targetAccounts.addedAt')}</th>
                      <th className="px-3 py-2" />
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {selected.members.map((m) => (
                      <tr key={m.accountId} className="hover:bg-muted/20">
                        <td className="px-3 py-2 font-medium">{m.accountName}</td>
                        <td className="px-3 py-2 text-muted-foreground">{m.industry ?? '—'}</td>
                        <td className="px-3 py-2 text-muted-foreground">{m.sizeBucket ?? '—'}</td>
                        <td className="px-3 py-2 text-muted-foreground">
                          {new Date(m.addedAt).toLocaleDateString()}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <button
                            onClick={() => removeMember(selected.id, m.accountId)}
                            className="text-xs text-destructive/70 hover:text-destructive"
                          >
                            {t('targetAccounts.removeAccount')}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
